/**
 * Admin Dashboard Constants
 * Centralized configuration and magic strings
 */

import { isAdminSubdomain } from '../../utils/navigation';

// Filter periods
export const FILTER_PERIODS = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
  ALL: 'all'
};

// All platforms options
export const ALL_PLATFORMS = [
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'solana', label: 'Solana' },
  { value: null, label: 'All Platforms' }
];

// Comparison modes for metrics
export const COMPARISON_MODES = {
  NONE: 'none',
  PREVIOUS: 'previous',
  PERCENTAGE: 'percentage'
};

// LocalStorage keys
export const STORAGE_KEYS = {
  FILTER_PERIOD: 'admin-filter-period',
  FILTER_PLATFORM: 'admin-filter-platform',
  FILTER_STATUS: 'admin-filter-status',
  AUTH: 'adminAuth',
  ADMIN_EMAIL: 'admin-email',
  SIDEBAR_COLLAPSED: 'admin-sidebar-collapsed',
  EXPANDED_METRICS: 'admin-expanded-metrics',
  AUTO_REFRESH: 'admin-auto-refresh',
  AUTO_REFRESH_INTERVAL: 'admin-auto-refresh-interval',
  COMPACT_MODE: 'admin-compact-mode',
  TABLE_ITEMS_PER_PAGE: 'admin-table-items-per-page',
  DATE_FORMAT: 'admin-date-format',
  CURRENCY_FORMAT: 'admin-currency-format',
  SHOW_TOOLTIPS: 'admin-show-tooltips',
  CHART_ANIMATIONS: 'admin-chart-animations',
  DASHBOARD_WIDGETS: 'admin-dashboard-widgets',
  NUMBER_FORMAT: 'admin-number-format'
};

// Re-export isAdminSubdomain from utils for backward compatibility
export { isAdminSubdomain };

// Get admin prefix based on subdomain
export const getAdminPrefix = () => isAdminSubdomain() ? '' : '/admin';

// Route paths - base paths (will be prefixed dynamically)
export const ROUTES_BASE = {
  DASHBOARD: '/dashboard',
  ANALYTICS: '/analytics',
  USERS: '/users',
  USER_SEGMENTS: '/users/segments',
  USER_LIFECYCLE: '/users/lifecycle',
  USER_BEHAVIOR: '/users/behavior',
  COMMUNICATION: '/communication',
  SPLITS: '/splits',
  TRANSACTIONS: '/transactions',
  WALLETS: '/wallets',
  BADGES: '/badges',
  WAITLIST: '/waitlist',
  SETTINGS: '/settings',
  LOGIN: '/login'
};

// Route paths - dynamically generated
export const ROUTES = {
  get DASHBOARD() { return `${getAdminPrefix()}${ROUTES_BASE.DASHBOARD}`; },
  get ANALYTICS() { return `${getAdminPrefix()}${ROUTES_BASE.ANALYTICS}`; },
  get USERS() { return `${getAdminPrefix()}${ROUTES_BASE.USERS}`; },
  get USER_SEGMENTS() { return `${getAdminPrefix()}${ROUTES_BASE.USER_SEGMENTS}`; },
  get USER_LIFECYCLE() { return `${getAdminPrefix()}${ROUTES_BASE.USER_LIFECYCLE}`; },
  get USER_BEHAVIOR() { return `${getAdminPrefix()}${ROUTES_BASE.USER_BEHAVIOR}`; },
  get COMMUNICATION() { return `${getAdminPrefix()}${ROUTES_BASE.COMMUNICATION}`; },
  get SPLITS() { return `${getAdminPrefix()}${ROUTES_BASE.SPLITS}`; },
  get TRANSACTIONS() { return `${getAdminPrefix()}${ROUTES_BASE.TRANSACTIONS}`; },
  get WALLETS() { return `${getAdminPrefix()}${ROUTES_BASE.WALLETS}`; },
  get BADGES() { return `${getAdminPrefix()}${ROUTES_BASE.BADGES}`; },
  get WAITLIST() { return `${getAdminPrefix()}${ROUTES_BASE.WAITLIST}`; },
  get SETTINGS() { return `${getAdminPrefix()}${ROUTES_BASE.SETTINGS}`; },
  get LOGIN() { return `${getAdminPrefix()}${ROUTES_BASE.LOGIN}`; }
};

// Auto-refresh configuration
export const REFRESH_CONFIG = {
  INTERVAL: 30000, // 30 seconds
  ENABLED: true
};

// Toast notification durations
export const TOAST_DURATIONS = {
  SHORT: 2000,
  MEDIUM: 4000,
  LONG: 6000
};

// Default values
export const DEFAULTS = {
  PERIOD: FILTER_PERIODS.DAY,
  PLATFORM: null,
  STATUS: null,
  COMPACT: false
};

// Page titles and descriptions
export const PAGE_INFO = {
  DASHBOARD: {
    title: 'Dashboard',
    description: 'Overview of key metrics and performance'
  },
  ANALYTICS: {
    title: 'Analytics',
    description: 'Comprehensive analytics and insights'
  },
  USERS: {
    title: 'Users',
    description: 'User management and statistics'
  },
  SPLITS: {
    title: 'Splits',
    description: 'Split activity and analytics'
  },
  TRANSACTIONS: {
    title: 'Transactions',
    description: 'Transaction history and metrics'
  },
  WALLETS: {
    title: 'Wallets',
    description: 'Wallet analytics and shared wallets'
  },
  BADGES: {
    title: 'Badges',
    description: 'Badge system and user achievements'
  },
  WAITLIST: {
    title: 'Waitlist',
    description: 'Waitlist management and subscribers'
  },
  SETTINGS: {
    title: 'Settings',
    description: 'Dashboard and app configuration'
  },
  USER_SEGMENTS: {
    title: 'User Segments',
    description: 'Segment users by engagement, lifecycle, feature usage, and value'
  },
  USER_LIFECYCLE: {
    title: 'User Lifecycle',
    description: 'Track user progression through signup to active user'
  },
  USER_BEHAVIOR: {
    title: 'User Behavior',
    description: 'Analyze feature adoption, usage patterns, and preferences'
  },
  COMMUNICATION: {
    title: 'Communication Analytics',
    description: 'Measure effectiveness of email, push notifications, and in-app messaging'
  }
};

// Analytics tabs configuration
export const ANALYTICS_TABS = [
  { id: 'overview', label: 'Overview', iconClass: 'bi-graph-up' },
  { id: 'financial', label: 'Financial', iconClass: 'bi-cash-coin' },
  { id: 'users', label: 'Users', iconClass: 'bi-people' },
  { id: 'splits', label: 'Splits', iconClass: 'bi-scissors' },
  { id: 'transactions', label: 'Transactions', iconClass: 'bi-credit-card' },
  { id: 'notifications', label: 'Notifications', iconClass: 'bi-bell' },
  { id: 'wallets', label: 'Wallets', iconClass: 'bi-wallet2' },
  { id: 'waitlist', label: 'Waitlist', iconClass: 'bi-card-checklist' }
];

// Navigation menu items - dynamically generated
export const getNavMenuItems = () => {
  const prefix = getAdminPrefix();
  return [
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.DASHBOARD}`, 
      iconClass: 'bi-speedometer2', 
      label: 'Dashboard',
      group: 'Main'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.USERS}`, 
      iconClass: 'bi-people', 
      label: 'Users',
      group: 'Main'
    },
    { 
      type: 'divider',
      label: 'Features',
      group: 'Features'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.SPLITS}`, 
      iconClass: 'bi-scissors', 
      label: 'Splits',
      group: 'Features'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.TRANSACTIONS}`, 
      iconClass: 'bi-credit-card', 
      label: 'Transactions',
      group: 'Features'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.WALLETS}`, 
      iconClass: 'bi-wallet2', 
      label: 'Wallets',
      group: 'Features'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.BADGES}`, 
      iconClass: 'bi-award', 
      label: 'Badges',
      group: 'Features'
    },
    { 
      type: 'divider',
      label: 'Communication',
      group: 'Communication'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.USER_SEGMENTS}`, 
      iconClass: 'bi-diagram-3', 
      label: 'User Segments',
      group: 'Communication'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.USER_LIFECYCLE}`, 
      iconClass: 'bi-arrow-repeat', 
      label: 'User Lifecycle',
      group: 'Communication'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.USER_BEHAVIOR}`, 
      iconClass: 'bi-activity', 
      label: 'User Behavior',
      group: 'Communication'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.COMMUNICATION}`, 
      iconClass: 'bi-envelope', 
      label: 'Communication',
      group: 'Communication'
    },
    { 
      type: 'divider',
      label: 'Tools',
      group: 'Tools'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.ANALYTICS}`, 
      iconClass: 'bi-bar-chart', 
      label: 'Analytics',
      group: 'Tools'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.WAITLIST}`, 
      iconClass: 'bi-card-checklist', 
      label: 'Waitlist',
      group: 'Tools'
    },
    { 
      type: 'link',
      path: `${prefix}${ROUTES_BASE.SETTINGS}`, 
      iconClass: 'bi-gear', 
      label: 'Settings',
      group: 'Tools'
    }
  ];
};

// For backward compatibility
export const NAV_MENU_ITEMS = getNavMenuItems();

// Bento grid tile sizing
export const BENTO_SIZES = {
  SMALL: { cols: 4, rows: 1 },
  MEDIUM: { cols: 6, rows: 2 },
  LARGE: { cols: 12, rows: 2 }
};

/**
 * Get bento tile size based on card count
 * @param {number} count - Number of cards in the section
 * @returns {Object} - Bento size configuration
 */
export const getBentoSizeFromCount = (count) => {
  if (count <= 3) return BENTO_SIZES.SMALL;
  if (count <= 6) return BENTO_SIZES.MEDIUM;
  return BENTO_SIZES.LARGE;
};

